import React, { Component } from "react";
import Select from "react-select";
import { Link } from 'react-router-dom';
import Loader from "react-loaders";
import backlogo from '../../assets/images/svg/Back.svg';
import axiosInstance from "../configuration/Request";
import { AiOutlineDownload, AiOutlineSearch } from "react-icons/ai";
import { FiRotateCcw } from "react-icons/fi";
import PrognosisGeneDetailPanel from "../ReusableComponent/PrognosisGeneDetailPanel";
import { roundedSelectStyles } from "../ReusableComponent/biomarkerTableStyles";
import PrognosisResultsTable from "../ReusableComponent/PrognosisResultsTable";
import { getPrognosisContextColumns, getPrognosisTableGroups, getPrognosisMetric } from "../ReusableComponent/prognosisTableModel";

const CANCER_TYPE_MAP = {
  'NSCLC': 'Non-Small Cell Lung Cancer',
  'SCLC': 'Small Cell Lung Cancer',
};



class BioMarkerResultPage extends Component {
  constructor(props) {
    super(props);

    const locationState = props.location?.state || {};
    const { prognosisDetails, bioMarker } = locationState;
    const searchParams = new URLSearchParams(props.location?.search || '');
    const queryMolecularTypes = searchParams.get('molecularType');
    const incomingCohort = searchParams.get('cohort') || prognosisDetails?.cohort || null;
    const requestedCohortEnabled = searchParams.has('cohortEnabled')
      ? searchParams.get('cohortEnabled') === 'true'
      : !!prognosisDetails?.cohortEnabled;
    const requestedPopulation = searchParams.has('population')
      ? searchParams.get('population') === 'true'
      : !!prognosisDetails?.population;
    const incomingConcordant = searchParams.has('concordant')
      ? searchParams.get('concordant') === 'true'
      : !!prognosisDetails?.concordant;
    const incomingCohortEnabled = incomingConcordant || requestedCohortEnabled;
    const incomingPopulation = incomingConcordant || (requestedPopulation && !incomingCohortEnabled);
    const incomingPrognosis = searchParams.get('prognosisType') || prognosisDetails?.prognosisType || 'good';
    const incomingMolecularTypes = queryMolecularTypes
      ? queryMolecularTypes.split(',').map(type => type.trim()).filter(Boolean)
      : prognosisDetails?.expressionType || null;
    const incomingCancerType = searchParams.get('cancerType') || bioMarker?.label || 'NSCLC';

    this.state = {
      cohortOptions: [],
      molecularTypeOptions: [],
      prognosisSelection: incomingPrognosis,
      // initialize cohort and optional flags from navigation payload when provided
      selectedCohort: incomingCohortEnabled ? incomingCohort : null,
      selectedCohortEnabled: incomingCohortEnabled,
      selectedPopulationEnabled: incomingPopulation,
      selectedConcordantEnabled: incomingConcordant,
      molecularTypeCheckboxes: {},
      genePanel: null,
      tableData: [],
      totalRows: 0,
      bioMarker: bioMarker || { label: incomingCancerType },
      initialExpressionType: incomingMolecularTypes,
      initialSelectedCohort: incomingCohort,
      initialSelectedCohortEnabled: incomingCohortEnabled,
      initialSelectedPopulationEnabled: incomingPopulation,
      initialSelectedConcordantEnabled: incomingConcordant,
      initialFilters: null,
      loader: false,
      dropdownLoader: true,
      searchTerm: '',
      significantOnly: false,
      resultMode: incomingConcordant ? 'concordant' : incomingPopulation ? 'population' : incomingCohortEnabled ? 'cohort' : 'all',
    };
  }

  componentDidMount() {
    this.fetchDropdowns();
  }

  fetchDropdowns = () => {
    this.setState({ dropdownLoader: true });
    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/gene-dropdown/projection-dropdowns`)
      .then(res => {
        const prognosis = res.data?.dropdowns?.prognosis || {};
        const cohortOptions = prognosis.cohort || [];
        const molecularTypeOptions = prognosis.data_level || [];

        const defaultCohort = this.state.initialSelectedCohort ||
          (cohortOptions.includes('TCGA') ? 'TCGA' : (cohortOptions[0] || null));

        const defaultApiLevel = this.state.initialExpressionType;
        const molecularTypeCheckboxes = {};
        molecularTypeOptions.forEach((t, i) => {
          if (defaultApiLevel) {
            molecularTypeCheckboxes[t] = Array.isArray(defaultApiLevel)
              ? defaultApiLevel.includes(t)
              : t === defaultApiLevel;
          } else {
            molecularTypeCheckboxes[t] = i === 0;
          }
        });
        if (molecularTypeOptions.length > 0 && !Object.values(molecularTypeCheckboxes).some(Boolean)) {
          molecularTypeCheckboxes[molecularTypeOptions[0]] = true;
        }

        const initialFilters = {
          prognosisSelection: this.state.prognosisSelection,
          // preserve incoming cohort selection/enabled flag when present
          selectedCohort: this.state.initialSelectedCohort,
          selectedCohortEnabled: this.state.initialSelectedCohortEnabled || false,
          selectedPopulationEnabled: this.state.initialSelectedPopulationEnabled || false,
          selectedConcordantEnabled: this.state.initialSelectedConcordantEnabled || false,
          molecularTypeCheckboxes: { ...molecularTypeCheckboxes },
        };

        this.setState({
          cohortOptions,
          molecularTypeOptions,
          // apply incoming cohort and optional flags if enabled, otherwise keep defaults
          selectedCohort: this.state.initialSelectedCohortEnabled ? this.state.initialSelectedCohort : null,
          selectedCohortEnabled: !!this.state.initialSelectedCohortEnabled,
          selectedPopulationEnabled: !!this.state.initialSelectedPopulationEnabled,
          selectedConcordantEnabled: !!this.state.initialSelectedConcordantEnabled,
          molecularTypeCheckboxes,
          initialFilters,
          dropdownLoader: false,
        }, this.fetchTableData);
      })
      .catch(err => {
        console.log(err);
        this.setState({ dropdownLoader: false });
      });
  };

  fetchTableData = () => {
    const { prognosisSelection, selectedCohort, selectedCohortEnabled, molecularTypeCheckboxes, bioMarker } = this.state;

    const selectedTypes = Object.keys(molecularTypeCheckboxes).filter(k => molecularTypeCheckboxes[k]);

    if (selectedTypes.length === 0) return;
    if (selectedCohortEnabled && !selectedCohort) return;

    const rawLabel = bioMarker?.label || 'NSCLC';
    const cancerType = CANCER_TYPE_MAP[rawLabel] || rawLabel;
    const prognosisType = prognosisSelection === 'good' ? 'Good prognosis' : 'Poor prognosis';

    this.setState({ loader: true, tableData: [], totalRows: 0, genePanel: null });

    const payloadData = { cancer_type: cancerType, prognosis_type: prognosisType, molecular_type: selectedTypes };
    const resultMode = this.state.selectedConcordantEnabled ? 'concordant' : this.state.selectedPopulationEnabled ? 'population' : selectedCohortEnabled ? 'cohort' : 'all';
    if (selectedCohortEnabled && selectedCohort) payloadData.cohorts = selectedCohort;
    if (this.state.selectedPopulationEnabled) payloadData.population = true;
    if (this.state.selectedConcordantEnabled) payloadData.concordant = true;
    if (this.state.significantOnly) payloadData.sig = true;

    axiosInstance.post(`${process.env.REACT_APP_BASE_URL}api/prognosis-projection/filter`, {
      data: payloadData
    })
      .then(res => {
        const data = res?.data?.data || [];
        const totalRows = Number.isFinite(Number(res?.data?.total))
          ? Number(res.data.total)
          : data.length;
        this.setState({ tableData: data, totalRows, resultMode });
      })
      .catch(err => {
        console.log(err);
        this.setState({ tableData: [], totalRows: 0 });
      })
      .finally(() => {
        this.setState({ loader: false });
      });
  };

  handlePrognosisChange = (e) => {
    this.setState({ prognosisSelection: e.target.value });
  };

  handleCohortChange = (selected) => {
    const selectedCohort = selected?.value || null;
    this.setState({
      selectedCohort,
    });
  };

  handleCohortToggle = () => {
    this.setState({
      selectedCohortEnabled: true,
      selectedPopulationEnabled: false,
      selectedConcordantEnabled: false,
    });
  };

  handleMolecularTypeChange = (e) => {
    const { name, checked } = e.target;
    this.setState(prev => ({
      molecularTypeCheckboxes: { ...prev.molecularTypeCheckboxes, [name]: checked }
    }));
  };

  handlePopulationToggle = () => {
    this.setState({
      selectedCohortEnabled: false,
      selectedCohort: null,
      selectedPopulationEnabled: true,
      selectedConcordantEnabled: false,
    });
  };

  handleConcordantToggle = () => {
    if (this.state.selectedConcordantEnabled) {
      this.handlePopulationToggle();
      return;
    }
    if (!this.state.selectedCohort) return;
    this.setState({
      selectedCohortEnabled: true,
      selectedPopulationEnabled: true,
      selectedConcordantEnabled: true,
    });
  };

  getRowKey = (row) => `${row.gene_name}|${row.cohort}|${row.data_level}|${row.histological_subtype}`;

  handleGeneClick = (row) => {
    const geneName = row.gene_name;
    this.setState({ genePanel: { geneName, row, mode: this.state.resultMode, rowKey: this.getRowKey(row) } });
  };

  closeGenePanel = () => this.setState({ genePanel: null });

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleSignificantOnlyChange = (e) => {
    this.setState({ significantOnly: e.target.checked }, this.fetchTableData);
  };

  updateFilterUrl = () => {
    const { prognosisSelection, selectedCohort, selectedCohortEnabled, selectedPopulationEnabled, selectedConcordantEnabled, molecularTypeCheckboxes } = this.state;
    const searchParams = new URLSearchParams(this.props.location?.search || '');
    searchParams.set('prognosisType', prognosisSelection);
    searchParams.set('molecularType', Object.keys(molecularTypeCheckboxes).filter(type => molecularTypeCheckboxes[type]).join(','));
    searchParams.set('cohortEnabled', String(!!selectedCohortEnabled));
    searchParams.set('population', String(!!selectedPopulationEnabled));
    searchParams.set('concordant', String(!!selectedConcordantEnabled));
    if (selectedCohort) searchParams.set('cohort', selectedCohort);
    else searchParams.delete('cohort');
    this.props.history.replace({ pathname: this.props.location.pathname, search: `?${searchParams.toString()}` });
  };

  onCancel = () => {
    const { initialFilters } = this.state;
    if (!initialFilters) return;
    this.setState({
      prognosisSelection: initialFilters.prognosisSelection,
      selectedCohort: initialFilters.selectedCohort,
      selectedCohortEnabled: initialFilters.selectedCohortEnabled || false,
      selectedPopulationEnabled: initialFilters.selectedPopulationEnabled || false,
      selectedConcordantEnabled: initialFilters.selectedConcordantEnabled || false,
      molecularTypeCheckboxes: { ...initialFilters.molecularTypeCheckboxes },
      searchTerm: '',
      significantOnly: false,
    }, () => {
      this.updateFilterUrl();
      this.fetchTableData();
    });
  };

  onApply = () => {
    this.updateFilterUrl();
    this.fetchTableData();
  };

  handleDownloadCSV = () => {
    const { tableData } = this.state;
    if (!tableData.length) return;

    const escape = (val) => {
      const s = val == null ? '' : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const { resultMode } = this.state;
    const columns = [
      ...getPrognosisContextColumns(resultMode),
      ...getPrognosisTableGroups(tableData, resultMode).flatMap(group => group.columns.map(column => ({ ...column, label: `${group.label}: ${column.label}` }))),
      { label: 'Cancer Type', value: row => row.cancer_type },
    ];
    const header = columns.map(c => escape(c.label)).join(',');
    const rows = tableData.map(row =>
      columns.map(c => escape(c.value(row))).join(',')
    );
    const csv = [header, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Biomarker_Result_${this.getTableTitle().replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  isApplyDisabled = () => {
    const { selectedCohort, selectedCohortEnabled, molecularTypeCheckboxes, prognosisSelection, initialFilters } = this.state;
    // invalid if cohort enabled but not selected
    const missingCohort = selectedCohortEnabled && !selectedCohort;
    if (missingCohort) return true;
    // invalid if no molecular types selected
    if (!Object.values(molecularTypeCheckboxes).some(Boolean)) return true;

    // if we have initial filters, disable Apply when nothing changed
    if (initialFilters) {
      const samePrognosis = initialFilters.prognosisSelection === prognosisSelection;
      const sameCohortEnabled = !!initialFilters.selectedCohortEnabled === !!selectedCohortEnabled;
      const sameCohortValue = (initialFilters.selectedCohort || null) === (selectedCohort || null);
      const samePopulation = !!initialFilters.selectedPopulationEnabled === !!this.state.selectedPopulationEnabled;
      const sameConcordant = !!initialFilters.selectedConcordantEnabled === !!this.state.selectedConcordantEnabled;
      const initMol = initialFilters.molecularTypeCheckboxes || {};
      const keys = Array.from(new Set([...Object.keys(initMol), ...Object.keys(molecularTypeCheckboxes)]));
      const sameMol = keys.every(k => !!initMol[k] === !!molecularTypeCheckboxes[k]);

      if (samePrognosis && sameCohortEnabled && sameCohortValue && samePopulation && sameConcordant && sameMol) return true;
    }

    return false;
  };

  getTableTitle = () => {
    const { prognosisSelection, resultMode } = this.state;
    const label = prognosisSelection === 'good' ? 'Good prognosis' : 'Poor prognosis';
    return `${label}${resultMode === 'concordant' ? ' concordant' : ''} results`;
  };

  getFilteredData = () => {
    const { tableData, searchTerm } = this.state;
    const term = searchTerm.trim().toLowerCase();
    return tableData.filter(row => {
      if (term && !String(row.gene_name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  };

  render() {
    const {
      prognosisSelection, cohortOptions, molecularTypeOptions,
      selectedCohort, molecularTypeCheckboxes, selectedPopulationEnabled, selectedConcordantEnabled,
      genePanel,
      bioMarker, tableData, resultMode, loader, dropdownLoader,
      searchTerm, significantOnly,
    } = this.state;

    const cancerTypeLabel = bioMarker?.label || 'NSCLC';
    const filteredData = this.getFilteredData();
    const groups = getPrognosisTableGroups(tableData, resultMode);
    const seerVersions = [...new Set(tableData.map(row => getPrognosisMetric(row, 'projection', 'seer_version', resultMode)).filter(value => value !== null && value !== ''))];

    return (
      <>
        <div className="bmt-page">
          <Link className="bmt-back-link" to={{ pathname: '/app/bioMarker' }}>
            <img src={backlogo} alt="" />
            <span>Back</span>
          </Link>

          <div className="bmt-layout">
            <div className="bmt-filter-col">
              <div className="bmt-filter-card">
                <div className="bmt-flow-badge">Biomarker Browser · By Prognosis</div>
                <div className="bmt-filter-title">Filters</div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Cancer type</span>
                  <div className="bmt-static-field">{cancerTypeLabel}</div>
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Prognosis</span>
                  <div className="bmt-radio-group">
                    {[{ value: 'good', label: 'Good prognosis' }, { value: 'poor', label: 'Poor prognosis' }].map(opt => (
                      <label key={opt.value} className="bmt-radio-label">
                        <input
                          type="radio"
                          name="resultPrognosis"
                          value={opt.value}
                          checked={prognosisSelection === opt.value}
                          onChange={this.handlePrognosisChange}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Molecular type</span>
                  <div className="bmt-checkbox-grid">
                    {molecularTypeOptions.map(type => (
                      <label key={type} className="bmt-checkbox-label">
                        <input
                          type="checkbox"
                          name={type}
                          checked={!!molecularTypeCheckboxes[type]}
                          onChange={this.handleMolecularTypeChange}
                        />
                        {type}
                      </label>
                    ))}
                    {molecularTypeOptions.length === 0 && !dropdownLoader && (
                      <span style={{ fontSize: '0.75rem', color: '#aaa', gridColumn: '1 / -1' }}>No options available</span>
                    )}
                  </div>
                </div>

                <div className="bmt-filter-field">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label className="bmt-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="radio"
                        name="resultCohort"
                        checked={!!this.state.selectedCohortEnabled}
                        onClick={selectedConcordantEnabled ? this.handleCohortToggle : undefined}
                        onChange={this.handleCohortToggle}
                      />
                      <span style={{ fontWeight: 600 }}>Cohort</span>
                    </label>
                  </div>
                  <Select
                    isDisabled={!this.state.selectedCohortEnabled}
                    options={cohortOptions.map(c => ({ value: c, label: c }))}
                    value={selectedCohort ? { value: selectedCohort, label: selectedCohort } : null}
                    onChange={this.handleCohortChange}
                    placeholder="Select cohort..."
                    styles={roundedSelectStyles}
                  />
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label className="bmt-checkbox-label">
                      <input type="radio" name="resultPopulation" checked={!!this.state.selectedPopulationEnabled} onClick={selectedConcordantEnabled ? this.handlePopulationToggle : undefined} onChange={this.handlePopulationToggle} />{' '}
                      <span style={{fontWeight: 600 }}>Population Projection</span>
                    </label>
                    <label className="bmt-checkbox-label">
                      <input type="radio" name="resultConcordant" disabled={!selectedCohort} checked={!!this.state.selectedConcordantEnabled} readOnly onClick={this.handleConcordantToggle} />{' '}
                      <span >Concordant Results of Selected Cohort and Population Projection</span>
                    </label>
                  </div>
                </div>

                <div className="bmt-filter-actions">
                  <button type="button" className="bmt-btn-reset" onClick={this.onCancel}>
                    <FiRotateCcw size={14} />
                    Reset
                  </button>
                  <button type="button" className="bmt-btn-apply" onClick={this.onApply} disabled={this.isApplyDisabled()}>
                    Apply filters
                  </button>
                </div>
              </div>
            </div>

            <div className="bmt-results-col">
              <div className="bmt-results-card prognosis-results-card">
                <div className="bmt-results-header">
                  <div>
                    <h2 className="bmt-results-title">{this.getTableTitle()}</h2>
                    <div className="bmt-results-subtitle">{resultMode === 'concordant' ? 'Selected cohort and population projection' : resultMode === 'population' ? 'Population projection associations' : 'Biomarker associations by prognosis'}</div>
                  </div>
                  <button
                    type="button"
                    className="bmt-btn-download"
                    onClick={this.handleDownloadCSV}
                    disabled={tableData.length === 0}
                  >
                    <AiOutlineDownload size={16} />
                    Download CSV
                  </button>
                </div>

                <div className="bmt-chip-row">
                  <span className="bmt-chip">{cancerTypeLabel}</span>
                  {this.state.selectedCohortEnabled && selectedCohort && <span className="bmt-chip">{selectedCohort}</span>}
                  {Object.keys(molecularTypeCheckboxes).filter(type => molecularTypeCheckboxes[type]).map(type => <span key={type} className="bmt-chip">{type}</span>)}
                  {seerVersions.map(version => <span key={version} className="bmt-chip">SEER {version}</span>)}
                </div>

                {(loader || dropdownLoader) ? null : tableData.length === 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', color: '#888', fontSize: '1rem' }}>
                    No data found for the selected filters.
                  </div>
                ) : (
                  <>
                    <div className="bmt-toolbar">
                      <div className="tp-search-wrap">
                        <AiOutlineSearch className="tp-search-icon" size={14} />
                        <input
                          type="text"
                          placeholder="Search genes"
                          aria-label="Search prognosis genes"
                          value={searchTerm}
                          onChange={this.handleSearchChange}
                        />
                      </div>
                      <label className="bmt-checkbox-label">
                        <input type="checkbox" checked={significantOnly} onChange={this.handleSignificantOnlyChange} />
                        Significant only
                      </label>
                      <span className="tp-badge" role="status">{filteredData.length} result{filteredData.length === 1 ? '' : 's'}</span>
                      <div className="prognosis-legend" aria-label="Column groups">
                        {groups.map(group => <span key={group.id} className={`metric-${group.id}`}><i aria-hidden="true" />{group.label.replace(' Cox model', '')}</span>)}
                      </div>
                    </div>

                    <PrognosisResultsTable
                      rows={filteredData}
                      groups={groups}
                      mode={resultMode}
                      selectedRowKey={genePanel?.rowKey}
                      getRowKey={this.getRowKey}
                      onGeneClick={this.handleGeneClick}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {genePanel && (
          <PrognosisGeneDetailPanel
            key={genePanel.rowKey}
            geneName={genePanel.geneName}
            row={genePanel.row}
            mode={genePanel.mode}
            onClose={this.closeGenePanel}
          />
        )}

        {(loader || dropdownLoader) && (
          <div className="backdrop">
            <div className="loader">
              <Loader type="ball-pulse-rise" />
            </div>
          </div>
        )}
      </>
    );
  }
}

export default BioMarkerResultPage;
